import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { t } from '../i18n.js';

/**
 * "Zoek over alles" uit ADTS-ONE (Zoeker.jsx daar), hier op de gegevens van ZERO.
 *
 * **Bewust geen apart zoekendpoint.** Dat zou een tweede plek worden die bepaalt wie wat mag zien, en die
 * loopt uiteen met de eerste. Daarom vraagt dit de gewone lijst-endpoints: de afscherming is precies dezelfde
 * als wanneer je de lijst zelf opent. De zoekopdracht gaat mee met de klant die je bekijkt.
 *
 * Vier bronnen: een adres (IP) stuurt je direct naar de kaart, en verder alerts, bronnen en klanten. Een
 * klant kiezen zet de klantfilter (hetzelfde als in het gebruikersmenu) en opent het overzicht.
 */
const isIp = (s) => /^\d{1,3}(\.\d{1,3}){3}$/.test(s) || (/^[0-9a-fA-F:]+$/.test(s) && s.includes(':'));

export default function Zoeker({ mag, klant, tenants = [], zetKlant }) {
  const [vraag, zetVraag] = useState('');
  const [uitkomst, zetUitkomst] = useState(null);
  const veld = useRef(null);
  const naar = useNavigate();

  /* De bronnen in het veld; `label` is ook de kop boven de groep (en wordt door de vertaalcontrole gezien). */
  const bronnen = [
    { id: 'kaart', label: 'Kaart', recht: 'alerts.lezen',
      haal: (term) => (isIp(term) ? [{ ip: term }] : []),
      kies: (r) => naar(`/kaart?q=${encodeURIComponent(r.ip)}`),
      links: () => '', midden: (r) => t('{ip} op de kaart', { ip: r.ip }), rechts: () => '' },
    { id: 'alerts', label: 'Alerts', recht: 'alerts.lezen',
      /* De server zoekt in alle alerts, ook in waarden als een adres, gebruiker of klant, en in notities. */
      haal: (term, kl) => api.alerts(kl, 'alle', term).then((r) => (r.data.alerts ?? []).slice(0, 6)),
      kies: (a) => naar(`/alerts/${a.number}`),
      links: (a) => a.number, midden: (a) => t(a.title.key, a.title.params), rechts: (a) => a.tenant?.name ?? '' },
    { id: 'bronnen', label: 'Bronnen', recht: 'alerts.lezen',
      /* Bronnen heeft geen zoekparameter; de lijst is kort, dus filteren we op naam en adres in het scherm. */
      haal: async (term, kl) => {
        const r = await api.bronnen(kl);
        return (r.data.sources ?? [])
          .filter((s) => (s.name ?? '').toLowerCase().includes(term) || (s.syslogIp ?? s.host ?? '').toLowerCase?.().includes(term))
          .slice(0, 6);
      },
      kies: (s) => naar(`/bronnen/${s.id}`),
      links: () => '', midden: (s) => s.name, rechts: (s) => s.tenant?.name ?? '' },
    { id: 'klanten', label: 'Klanten', recht: 'alerts.lezen',
      /* De klanten die je mag zien staan al in `mij.tenants`; kiezen zet de klantfilter en opent het overzicht. */
      haal: (term) => tenants.filter((kl) => (kl.name ?? '').toLowerCase().includes(term) || (kl.code ?? '').toLowerCase().includes(term)).slice(0, 6),
      kies: (kl) => { zetKlant?.(String(kl.id)); naar('/'); },
      links: (kl) => kl.code ?? '', midden: (kl) => kl.name, rechts: (kl) => (kl.own ? t('(eigen)') : '') },
  ];

  useEffect(() => {
    const sneltoets = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); veld.current?.focus(); }
      if (e.key === 'Escape') zetUitkomst(null);
    };
    window.addEventListener('keydown', sneltoets);
    return () => window.removeEventListener('keydown', sneltoets);
  }, []);

  useEffect(() => {
    const term = vraag.trim().toLowerCase();
    if (term.length < 2) { zetUitkomst(null); return undefined; }
    let afgebroken = false;
    /* Wachten tot iemand klaar is met typen: anders is elke aanslag een verzoek. */
    const timer = setTimeout(async () => {
      const toegestaan = bronnen.filter((b) => mag(b.recht));
      /* Naast elkaar en niet na elkaar: losse vragen die niets van elkaar weten. Een die faalt, geeft een lege
         groep en houdt de rest niet tegen. Promise.resolve: een bron mag ook synchroon antwoorden. */
      const uit = await Promise.all(toegestaan.map((b) => Promise.resolve(b.haal(term, klant)).catch(() => [])));
      if (!afgebroken) zetUitkomst(toegestaan.map((b, i) => ({ bron: b, rijen: uit[i] ?? [] })));
    }, 250);
    return () => { afgebroken = true; clearTimeout(timer); };
  }, [vraag, mag, klant, tenants]);

  const kies = (bron, rij) => { zetVraag(''); zetUitkomst(null); bron.kies(rij); };
  const leeg = uitkomst && uitkomst.every((g) => !g.rijen.length);

  return (
    <div className="gsearch">
      <span aria-hidden="true" style={{ opacity: .7 }}>⌕</span>
      <input ref={veld} value={vraag} onChange={(e) => zetVraag(e.target.value)}
        placeholder={t('Zoek over alles')} aria-label={t('Zoek over alles')} />
      <span className="kbd">⌘K</span>
      {uitkomst && (
        <div className="gresults">
          {leeg && <div className="leeg">{t('Niets gevonden.')}</div>}
          {uitkomst.filter((g) => g.rijen.length).map(({ bron, rijen }) => (
            <div key={bron.id}>
              <div className="groep">{t(bron.label)}</div>
              {rijen.map((r, i) => (
                <button key={`${bron.id}${r.id ?? i}`} type="button" onClick={() => kies(bron, r)}>
                  {bron.links(r) && <span className="num">{bron.links(r)}</span>}
                  <span>{bron.midden(r)}</span>
                  <span className="num" style={{ marginLeft: 'auto' }}>{bron.rechts(r)}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
